<template>
  <div class="cashflow-app">
    <transition name="fade">
      <div v-if="showToaster" class="toaster-container">
        <component :is="currentToasterComponent" v-bind="toasterProps" />
      </div>
    </transition>
    <div class="bars-container">
      <months-navigation-bar :disabled="blocked"/>
      <cashflow-state-bar v-if="barState"/>
    </div>
    <div v-if="showNotification || showCarousel">
      <notification v-if="showNotification" class="notification-wrapper"/>
      <insights-carousel v-else/>
      <separator/>
    </div>
    <div class="cashflow-container">
      <cashflow-view/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import EventBus from '@/event-bus/event-bus';
import BaseUI from 'base-ui';
import InsightFeedbackSentToaster
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/toasters/InsightFeedbackSentToaster';
import SupermarketSummary
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/supermarket-summary/SupermarketSummary';
import MidMonthEvent
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/midmonth/MidMonthEvent';
import Segment from '../../../../../Segment';
import CashflowView from './cashflow-view/CashflowView.vue';
import Notification from './notification/Notification.vue';
import InsightsCarousel from './notification/notification-types/InsightsCarousel.vue';
import casfhlowHistory from '../../../../../store/utilities/cashflow-history';
import MonthsNavigationBar from './cashflow-view/MonthsNavigationBar';
import CashflowStateBar from '../../../../../base-components/CashflowStateBar';
import ExpenseSummary from './cashflow-view/expense-summary/ExpenseSummary';

export default {
  name: 'CashflowApp',
  components: {
    InsightFeedbackSentToaster,
    CashflowView,
    Notification,
    InsightsCarousel,
    MonthsNavigationBar,
    CashflowStateBar,
    Separator: BaseUI.Separator,
  },
  data() {
    return {
      showToaster: false,
      currentToasterComponent: null,
      toasterProps: null,
    };
  },
  async created() {
    EventBus.$on('open-toaster',
      ({ component, props }) => {
        this.currentToasterComponent = component;
        this.toasterProps = props;
        this.showToaster = true;
      });
    EventBus.$on('close-toaster', () => { this.showToaster = false; });

    Segment.trackUserGot('CashflowViewEntered', { cashflowApp: true });
    this.startContentLoading(this.$options.name);
    await Promise.all([
      this.initBudgets({ budgetDate: this.budgetDate }),
      this.initApplicationState(),
      this.initInsights(),
    ]);
    if (this.notificationState === this.notificationTypes.ACCOUNT_QUESTIONS) {
      await this.fetchQuestions();
    }
    if (this.notificationState === this.notificationTypes.MERGE_QUESTIONS) {
      await this.fetchMergeQuestions();
    }
    if (this.$route.query.expenses) {
      const previousBudgetDate = casfhlowHistory.getPrevBudgetDate(this.budgetDate);
      const initialBudgetDate = this.budgetDate === casfhlowHistory.getCurrentBudgetDate() ? previousBudgetDate : this.budgetDate;
      this.openModal({
        component: ExpenseSummary,
        props: {
          initialBudgetDate,
        },
      });
    }
    if (this.$route.query.supermarket) {
      this.openModal({ component: SupermarketSummary });
    }
    if (this.$route.query.midmonth) {
      this.openModal({ component: MidMonthEvent });
    }
    this.doneContentLoading(this.$options.name);
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapGetters('cashflowView', ['budgetUpdatedAt', 'isPresentingCurrentMonth']),
    ...mapState('applicationState', ['notificationState', 'cashflowState', 'barState']),
    ...mapGetters('applicationState', ['notificationTypes', 'isActiveNotification', 'cashflowTypes']),
    ...mapState('insights', ['insights']),
    showNotification() {
      return this.isActiveNotification && this.isPresentingCurrentMonth;
    },
    showCarousel() {
      return !this.blocked && this.isPresentingCurrentMonth && this.anyCarouselCards;
    },
    anyCarouselCards() {
      return this.insights.length > 0;
    },
    blocked() {
      return this.cashflowState === this.cashflowTypes.BLOCKED;
    },
    budgetDate() {
      const currentBudgetDate = casfhlowHistory.getCurrentBudgetDate();
      return this.$route.query.msg === 'eom' ? casfhlowHistory.getPrevBudgetDate(currentBudgetDate) : currentBudgetDate;
    },
  },
  methods: {
    ...mapActions('contentLoader', ['startContentLoading', 'doneContentLoading']),
    ...mapActions('applicationState', ['initApplicationState']),
    ...mapActions('cashflowView', ['initBudgets']),
    ...mapActions('accountInvestigationQuestions', ['fetchQuestions']),
    ...mapActions('mergeInvestigationQuestions', ['fetchMergeQuestions']),
    ...mapActions('insights', ['initInsights']),
    ...mapActions('modalRootStore', ['openModal']),
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../../node_modules/base-ui/src/scss/riseup-colors';
@import '../../../../../scss/mixins';
@import '../../../../../scss/global-variables';

.cashflow-app {
  font-family: SimplerPro;

  .toaster-container{
    @include responsive-width-and-margin;
    position: fixed;
    display: flex;
    justify-content: center;
    padding: 0 17px;
    width: 100%;
    top: 75px;
    z-index: 40;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .bars-container {
    position: sticky;
    top: $app_header_height;
    z-index: 10;
  }

  .notification-wrapper {
    @include responsive-padding;
    padding-top: 30px;
    padding-bottom: 30px;
    background-color: $riseup_gray_-1;
  }

  .cashflow-container {
    padding-top: 30px;
    background-color: $riseup_white;
  }
}
</style>
